import React, { type FC, useEffect, useMemo, useRef, useState } from 'react';

import './_editPreviewModal.scss';
import '../EditVideo/_editVideoModal.scss';
import 'swiper/css';
import { useTranslation } from 'react-i18next';
import { GrFormPrevious } from 'react-icons/gr';
import {
  MdNavigateNext,
  MdOutlineFileUpload,
  MdOutlineImage,
  MdPlayCircleOutline
} from 'react-icons/md';
import ReactPlayer from 'react-player';
import ReactSlider from 'react-slider';
import BeatLoader from 'react-spinners/BeatLoader';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { IconCheckmarkSm, IconPauseFill, IconPlayOutline } from 'assets/svg/sprite';
import { Button } from 'components/Button/Button';
import { SwitchButton } from 'components-ref/atoms';
import { useOrientation } from 'helpers/helperHooks';
import { formatVideoTime } from 'lib/dayjs';
import { type ScrubFrame } from 'pages/newPost/generateScrubFrames'
import {
    applyCloudFrontCookies,
    type CloudFrontCredentials
} from 'helpers/cloudfrontCookies'

export type ConfirmPayload = {
  defaultPrev: string
  activeThumbnail: string | null
  thumbType: 'clip' | 'thumb'
  previewOn: boolean
  previewThumbnailIndex?: number | null
  customPreviewThumbKey?: string | null
  customPreviewThumbnailUrl?: string | null
  clip?: {
    start: number;
    end: number;
  };
  selectedIndex?: number;
  selectedTime?: number | null;
  customThumbKey?: string | null;
  customThumbCrop?: {
    x: number;
    y: number;
  } | null;
  previewVideoId?: string | null;
  customPreviewUrl?: string | null;
};

export type StoryboardData = {
    manifest: string
    base_url: string
    thumbnail_width: number
    thumbnail_height: number
    tile_width: number
    tile_height: number
    interval: number
    sheet_count: number
    frame_count: number
}
type StoryboardFrame = {
  url: string
  time: number
  sheetIndex: number
  tileIndex: number
}
interface Props {
    defaultPreview: string;
    storageSrc: string;
    duration?: number;
    orientation?: string;
    thumbnails?: string[];
    thumbnailTimes?: Array<number | null>;
    thumbnailIndex?: number;
    previewThumbnailIndex?: number | null;
    customPreviewThumbnailUrl?: string | null;
    customPreviewThumbKey?: string | null;
    trimPreview?: {
        start: number;
        end: number;
    };
    customPreviewUrl?: string | null;
    cloudfront?: CloudFrontCredentials | null;
    customPreviewCloudfront?: CloudFrontCredentials | null;
    previewVideoId?: string | null;
    uploadCustomPreview?: (
      file: File,
      signal?: AbortSignal
    ) => Promise<string>;
    thumbType?: 'clip' | 'thumb';
    initialPreviewOn?: boolean;
    customThumbnailUrl?: string | null;
    customThumbnailTime?: number | null;
    thumbsQueryDone?: boolean;
    cancelCb: () => void;
    confirmCb: (data: ConfirmPayload) => void;
    uploadCustomThumbnail: (file: File) => Promise<string>;
    uploadCustomPreviewThumbnail: (file: File) => Promise<string>;
    scrubFrames?: ScrubFrame[],
    storyboard?: StoryboardData | null
}
type CustomThumb = {
    url: string;
    time: number | null;
    sourceUrl?: string;
};
;

const MdOutlineFileUploadIcon = MdOutlineFileUpload as unknown as (props: {
  className?: string;
  size?: number | string;
}) => JSX.Element;

const GrPrevIcon = GrFormPrevious as unknown as (props: {
    className?: string;
    size?: number | string;
}) => JSX.Element;
const MdNextIcon = MdNavigateNext as unknown as (props: {
    className?: string;
    size?: number | string;
}) => JSX.Element;
const MdOutlineImageIcon = MdOutlineImage as unknown as (props: {
    className?: string;
    size?: number | string;
}) => JSX.Element;
const MdPlayCircleOutlineIcon = MdPlayCircleOutline as unknown as (props: {
    className?: string;
    size?: number | string;
}) => JSX.Element;
const FRAME_SKIP_SECONDS = 0.5
const DEFAULT_PREVIEW_LENGTH = 15
const MAX_PREVIEW_LENGTH = 15
const SCRUB_IDLE_EXIT_MS = 160
const SCRUB_ASSIST_ENTER_FRAME_RATIO = 0.35

const getScrubAssistThresholds = (
  duration: number,
  frameCount: number
) => {
  if (frameCount < 2 || duration <= 0) {
    return {
      frameGap: Infinity,
      enterTravel: Infinity
    }
  }

  const frameGap = duration / (frameCount - 1)

  return {
    frameGap,
    enterTravel: frameGap * SCRUB_ASSIST_ENTER_FRAME_RATIO
  }
}
const CUSTOM_CROP_WIDTH = 1280
const CUSTOM_CROP_HEIGHT = 720
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const EditPreviewModal: FC<Props> = ({
    defaultPreview,
    storageSrc: _storageSrc,
    duration,
    orientation,
    thumbnails = [],
    thumbnailTimes = [],
    thumbnailIndex = 0,
    previewThumbnailIndex = null,
    customPreviewThumbnailUrl,
    customPreviewThumbKey: initialCustomPreviewThumbKey,
    trimPreview,
    thumbType,
    cloudfront,
    customPreviewCloudfront,
    initialPreviewOn,
    customThumbnailUrl,
    customThumbnailTime,
    customPreviewUrl,
    previewVideoId: initialPreviewVideoId,
    thumbsQueryDone = true,
    cancelCb,
    confirmCb,
    uploadCustomThumbnail,
    uploadCustomPreviewThumbnail,
    uploadCustomPreview,
    scrubFrames = [],
    storyboard = null
  }) => {
    const { t } = useTranslation();
    const deviceOrientation = useOrientation();
    const playerRef = useRef<ReactPlayer>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const scrubMoveIdRef = useRef(0)
    const customThumbnailInputRef = useRef<HTMLInputElement>(null);
    const uploadedCropImageRef = useRef<HTMLImageElement>(null);
    const previewVideoInputRef = useRef<HTMLInputElement>(null);
    const previewThumbInputRef = useRef<HTMLInputElement>(null)
    const previewUploadControllerRef = useRef<AbortController | null>(null);
    const customPreviewBlobRef = useRef<string | null>(null);
    const keepPreviewBlobRef = useRef(false);
    
    const cropPointerRef = useRef<{
        pointerId: number;
        startX: number;
        startY: number;
        startPositionX: number;
        startPositionY: number;
    } | null>(null);
    const lastCommittedCoverRef = useRef<number | null>(null);
    const [activeTab, setActiveTab] = useState<'thumbnail' | 'videoPreview'>(
      thumbType === 'clip' ? 'videoPreview' : 'thumbnail'
    )
    const [thumbnailView, setThumbnailView] = useState<'select' | 'generate'>('select')
    const videoPreviewActive = activeTab === 'videoPreview'
    const generateThumbnailActive =
      activeTab === 'thumbnail' && thumbnailView === 'generate'
    const editorActive =
      videoPreviewActive || generateThumbnailActive

    const [videoPreviewView, setVideoPreviewView] = useState<
      'editor' | 'cover'
    >('editor')

    const [previewCoverIndex, setPreviewCoverIndex] = useState<number | null>(
      customPreviewThumbnailUrl ? null : previewThumbnailIndex
    )

    const [previewCoverUrl, setPreviewCoverUrl] = useState<string | null>(
      typeof previewThumbnailIndex === 'number'
        ? thumbnails[previewThumbnailIndex] ?? null
        : null
    )

    const [customPreviewThumbKey, setCustomPreviewThumbKey] =
      useState<string | null>(initialCustomPreviewThumbKey ?? null)

    const [customPreviewThumbUrl, setCustomPreviewThumbUrl] =
      useState<string | null>(customPreviewThumbnailUrl ?? null)

    const [customPreviewThumbUploading, setCustomPreviewThumbUploading] =
      useState(false)


    const [videoPreviewEnabled, setVideoPreviewEnabled] = useState(thumbType
        ? thumbType === 'clip'
        : initialPreviewOn ??
            false);
    const [playerReady, setPlayerReady] = useState(false);
    const [activeScrubFrame, setActiveScrubFrame] =
      useState<StoryboardFrame | ScrubFrame | null>(null)
    
const scrubRef = useRef<{
  time: number | null
  at: number
  speed: number
  gestureStartTime: number | null
  frameIndex: number | null
  direction: -1 | 0 | 1
  assisted: boolean
  enterCandidateAt: number | null
  exitCandidateAt: number | null
}>({
  time: null,
  at: 0,
  speed: 0,
  gestureStartTime: null,
  frameIndex: null,
  direction: 0,
  assisted: false,
  enterCandidateAt: null,
  exitCandidateAt: null
})
    const scrubIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [playing, setPlaying] = useState(false);
    const [ended, setEnded] = useState(false);
    const [noChanges, setNoChanges] = useState(true);
    const [videoDuration, setVideoDuration] = useState(typeof duration ===
        'number' &&
        Number.isFinite(duration) &&
        duration > 0
        ? duration
        : 0);
    const safeVideoDuration = Number.isFinite(videoDuration) &&
        videoDuration > 0
        ? videoDuration
        : 0;

    const storyboardFrames = useMemo<StoryboardFrame[]>(() => {
      if (!storyboard?.frame_count || !storyboard.interval) {
        return []
      }

      const framesPerSheet =
        storyboard.tile_width * storyboard.tile_height

      if (framesPerSheet <= 0) {
        return []
      }

      return Array.from(
        { length: storyboard.frame_count },
        (_, index) => {
          const sheetIndex = Math.floor(index / framesPerSheet)
          const tileIndex = index % framesPerSheet

          return {
            url:
              `${storyboard.base_url}Thumbnail_${String(
                sheetIndex + 1
              ).padStart(9, '0')}.jpg`,
            time: Math.min(
              index * storyboard.interval,
              Math.max(0, safeVideoDuration - 0.001)
            ),
            sheetIndex,
            tileIndex
          }
        }
      )
    }, [storyboard, safeVideoDuration])

    const activeScrubFrames =
      storyboardFrames.length > 0
        ? storyboardFrames
        : scrubFrames

    const scrubAssistThresholds = getScrubAssistThresholds(
      safeVideoDuration,
      activeScrubFrames.length
    )

    const [customPreviewBlob, setCustomPreviewBlob] = useState<string | null>(null);
    const [previewVideoId, setPreviewVideoId] = useState<string | null>(
      initialPreviewVideoId ?? null
    );
    const [existingPreviewUrl, setExistingPreviewUrl] = useState<string | null>(
      customPreviewUrl ?? null
    );
    const [customPreviewUploading, setCustomPreviewUploading] = useState(false);
    const [customPreviewDuration, setCustomPreviewDuration] = useState(0);

    const activePreviewVideo =
      customPreviewBlob ?? existingPreviewUrl ?? null;

    const usingCustomPreview =
      videoPreviewActive && !!activePreviewVideo;
      
    const playerUrl = usingCustomPreview
        ? activePreviewVideo
        : defaultPreview

    const playerCloudfront = usingCustomPreview
        ? customPreviewCloudfront
        : cloudfront

    const isProtectedHls =
        typeof playerUrl === 'string' &&
        playerUrl.startsWith('https://video-cdn.allfans.com/') &&
        playerUrl.split('?')[0].endsWith('.m3u8')

    const [authorizedPlayerUrl, setAuthorizedPlayerUrl] =
        useState<string | null>(null)

    useEffect(() => {
        if (!isProtectedHls || !playerUrl) {
            setAuthorizedPlayerUrl(null)
            return
        }

        const authorized =
            !!playerCloudfront &&
            applyCloudFrontCookies(playerCloudfront)

        if (!authorized) {
            console.error(
                '[EditPreviewModal] Missing or expired CloudFront credentials'
            )
        }

        setAuthorizedPlayerUrl(authorized ? playerUrl : null)
    }, [playerUrl, playerCloudfront, isProtectedHls])

    const canMountPlayer =
        !!playerUrl &&
        (!isProtectedHls || authorizedPlayerUrl === playerUrl)
    const initialThumbnailTime = thumbnailTimes[thumbnailIndex];
    const initialCoverTime = typeof customThumbnailTime ===
        'number'
        ? customThumbnailTime
        : typeof initialThumbnailTime ===
            'number'
            ? initialThumbnailTime
            : trimPreview?.start ??
                0;
    const [scrubTime, setScrubTime] = useState(thumbType === 'clip'
        ? trimPreview?.start ??
            0
        : initialCoverTime);
    const [currentTime, setCurrentTime] = useState(trimPreview?.start ??
        initialCoverTime);
    const [trimStart, setTrimStart] = useState(trimPreview?.start ??
        0);
    const [trimEnd, setTrimEnd] = useState(trimPreview?.end ??
        ((trimPreview?.start ??
            0) +
            DEFAULT_PREVIEW_LENGTH));
    const [sliderValues, setSliderValues] = useState<number[]>([
        trimPreview?.start ??
            0,
        trimPreview?.start ??
            0,
        trimPreview?.end ??
            ((trimPreview?.start ??
                0) +
                DEFAULT_PREVIEW_LENGTH)
    ]);
    const selectedClipDuration = Math.max(0, trimEnd -
        trimStart);
    const MIN_PREVIEW_LENGTH = 5

    const previewIsValidLength =
      selectedClipDuration >= MIN_PREVIEW_LENGTH
    const previewTooLong = selectedClipDuration >
        MAX_PREVIEW_LENGTH;
    const [thumbsReady, setThumbsReady] = useState(false);
    const [customThumbs, setCustomThumbs] = useState<CustomThumb[]>(() => {
        if (!customThumbnailUrl) {
            return [];
        }
        return [
            {
                url: customThumbnailUrl,
                time: typeof customThumbnailTime ===
                    'number'
                    ? customThumbnailTime
                    : null
            }
        ];
    });
    const [selectedTime, setSelectedTime] = useState<number | null>(typeof customThumbnailTime ===
        'number'
        ? customThumbnailTime
        : null);
    const [uploadedCustomThumbKey, setUploadedCustomThumbKey] = useState<string | null>(null);
    const [customThumbUploading, setCustomThumbUploading] = useState(false);
    const [uploadedLocalPreview, setUploadedLocalPreview] = useState<string | null>(null);
    const [uploadedCropActive, setUploadedCropActive] = useState(false);
    const [cropPosition, setCropPosition] = useState({
        x: 50,
        y: 50
    });
    const [cropDirty, setCropDirty] = useState(false);
    const [showPosterOverlay, setShowPosterOverlay] = useState(true);
    const [coverState, setCoverState] = useState<'initial' | 'toSet' | 'done'>('initial');
    const capturedCustomThumbs = customThumbs
        .filter(thumb => typeof thumb.time ===
        'number')
        .slice(0, 1)
        .map(thumb => ({
        url: thumb.url,
        type: 'custom' as const,
        originalIndex: undefined,
        time: thumb.time
    }));
    const uploadedCustomThumbs = customThumbs
        .filter(thumb => thumb.time ===
        null)
        .slice(0, 1)
        .map(thumb => ({
        url: thumb.url,
        sourceUrl: thumb.sourceUrl ??
            thumb.url,
        type: 'custom' as const,
        originalIndex: undefined,
        time: null
    }));
    const presetThumbs = thumbnails
        .slice(0, 8)
        .map((url, index) => ({
            url,
            type: 'preset' as const,
            originalIndex: index,
            time: typeof thumbnailTimes[index] ===
                'number'
                ? thumbnailTimes[index]
                : null
        }));
    const sliderThumbs = presetThumbs.length <= 6
      ? presetThumbs
      : Array.from({ length: 6 }, (_, index) => {
          const sourceIndex = Math.round(
            index * (presetThumbs.length - 1) / 5
          )
          return presetThumbs[sourceIndex]
        })
    const displayedThumbs = [
        ...capturedCustomThumbs,
        ...presetThumbs,
        ...uploadedCustomThumbs
    ];
    const [selectedPreviewIdx, setSelectedPreviewIdx] = useState(() => {
        if (customThumbnailUrl) {
            return 0;
        }
        if (!thumbnails.length) {
            return 0;
        }
        return clamp(thumbnailIndex, 0, Math.min(thumbnails.length, 8) - 1);
    });
    const selectedThumbItem = displayedThumbs[selectedPreviewIdx] ??
        null;
    const activeThumb = selectedThumbItem?.url ??
        null;
    const selectedUploadedCustomThumb = selectedThumbItem?.type ===
        'custom' &&
        selectedThumbItem.time ===
            null;
    const uploadedCropSource = uploadedCropActive
        ? uploadedLocalPreview ??
            (selectedUploadedCustomThumb
                ? selectedThumbItem?.url ?? null
                : null)
        : null;
    const hidePoster = () => {
        setShowPosterOverlay(false);
    };
    const showPoster = () => {
        setShowPosterOverlay(true);
    };
    const seekVideo = (time: number) => {
        const target = clamp(time, 0, safeVideoDuration ||
            time);
        playerRef.current?.seekTo(target, 'seconds');
    };
const getDirectionalScrubFrame = (
  target: number,
  direction: -1 | 1
): { frame: StoryboardFrame | ScrubFrame; index: number } | null => {
  const frames = activeScrubFrames

  if (!frames.length) return null

  if (direction === 1) {
    const index = frames.findIndex(
      frame => frame.time >= target
    )

    const resolvedIndex =
      index === -1 ? frames.length - 1 : index

    return {
      frame: frames[resolvedIndex],
      index: resolvedIndex
    }
  }

  for (let i = frames.length - 1; i >= 0; i--) {
    if (frames[i].time <= target) {
      return {
        frame: frames[i],
        index: i
      }
    }
  }

  return {
    frame: frames[0],
    index: 0
  }
}
const scrubTo = (target: number) => {
  const now = performance.now()
  const state = scrubRef.current
  const moveId = ++scrubMoveIdRef.current

  if (scrubIdleTimerRef.current) {
    clearTimeout(scrubIdleTimerRef.current)
  }

  const exitAssistWhenStopped = () => {
    const scheduledMoveId = moveId

    scrubIdleTimerRef.current = setTimeout(() => {
      if (scrubMoveIdRef.current !== scheduledMoveId) return

      const currentState = scrubRef.current

      if (!currentState.assisted) return

      setActiveScrubFrame(null)
      seekVideo(target)
    }, SCRUB_IDLE_EXIT_MS)
  }

  if (state.time === null) {
    state.time = target
    state.at = now
    state.speed = 0
    state.gestureStartTime = target

    seekVideo(target)
    return
  }

  const delta = target - state.time
  const elapsed = now - state.at

  if (!delta || elapsed <= 0) {
    exitAssistWhenStopped()
    return
  }

  const direction: -1 | 1 =
    delta > 0 ? 1 : -1

  state.time = target
  state.at = now

  if (state.gestureStartTime === null) {
    state.gestureStartTime = target
  }

  const gestureTravel = Math.abs(
    target - state.gestureStartTime
  )

  const shouldAssist =
    state.assisted ||
    (
      activeScrubFrames.length >= 2 &&
      gestureTravel >= scrubAssistThresholds.enterTravel
    )

  console.log(
    `[SCRUB SAMPLE] target=${target.toFixed(2)} ` +
    `travel=${gestureTravel.toFixed(2)} ` +
    `threshold=${
      Number.isFinite(scrubAssistThresholds.enterTravel)
        ? scrubAssistThresholds.enterTravel.toFixed(2)
        : '-'
    } ` +
    `frameGap=${
      Number.isFinite(scrubAssistThresholds.frameGap)
        ? scrubAssistThresholds.frameGap.toFixed(2)
        : '-'
    } ` +
    `frames=${activeScrubFrames.length} ` +
    `dir=${direction} ` +
    `assisted=${state.assisted} ` +
    `shouldAssist=${shouldAssist} ` +
    `frameIndex=${state.frameIndex ?? '-'} ` +
    `frameTime=${
      state.frameIndex !== null
        ? activeScrubFrames[state.frameIndex]?.time?.toFixed(2) ?? '-'
        : '-'
    }`
  )

  if (!shouldAssist || !activeScrubFrames.length) {
    state.assisted = false

    if (direction !== state.direction) {
      state.direction = direction
      state.frameIndex = null
    }

    if (activeScrubFrame) {
      setActiveScrubFrame(null)
    }

    seekVideo(target)
    return
  }

  if (direction !== state.direction) {
    state.direction = direction
    state.frameIndex = null
  }

  const result = getDirectionalScrubFrame(
    target,
    direction
  )

  if (!result) {
    state.assisted = false
    state.frameIndex = null

    setActiveScrubFrame(null)
    seekVideo(target)

    return
  }

  console.log('[SCRUB FRAME]', {
    target: Number(target.toFixed(2)),
    direction,
    selectedIndex: result.index,
    selectedTime: Number(
      result.frame.time.toFixed(2)
    ),
    previousIndex: state.frameIndex,
    previousTime:
      state.frameIndex !== null
        ? activeScrubFrames[state.frameIndex]?.time ?? null
        : null
  })

  if (
    state.frameIndex !== null &&
    (
      (
        direction === 1 &&
        result.index <= state.frameIndex
      ) ||
      (
        direction === -1 &&
        result.index >= state.frameIndex
      )
    )
  ) {
    exitAssistWhenStopped()
    return
  }

  state.assisted = true
  state.direction = direction
  state.frameIndex = result.index

  setActiveScrubFrame(result.frame)

  exitAssistWhenStopped()
}
    useEffect(() => {
        let cancelled = false;
        if (!thumbnails.length) {
            setThumbsReady(true);
            return;
        }
        setThumbsReady(false);
        const preload = async (src: string) => await new Promise<void>(resolve => {
            const img = new Image();
            const done = () => resolve();
            img.onload =
                done;
            img.onerror =
                done;
            img.src =
                src;
            if (img.complete) {
                resolve();
            }
        });
        void Promise.all(thumbnails.map(preload)).then(() => {
            if (!cancelled) {
                setThumbsReady(true);
            }
        });
        return () => {
            cancelled =
                true;
        };
    }, [
        thumbnails
    ]);
    useEffect(() => {
        if (customThumbnailUrl ||
            !thumbnails.length) {
            return;
        }
        setSelectedPreviewIdx(clamp(thumbnailIndex, 0, Math.min(thumbnails.length, 8) - 1));
    }, [
        thumbnailIndex,
        thumbnails,
        customThumbnailUrl
    ]);
    useEffect(() => {
        if (thumbType ===
            'clip') {
            setActiveTab('videoPreview');
            setVideoPreviewEnabled(true);
            return;
        }
        if (thumbType ===
            'thumb') {
            setActiveTab('thumbnail');
            setVideoPreviewEnabled(false);
        }
    }, [
        thumbType
    ]);
    useEffect(() => {
        if (!safeVideoDuration) {
            return;
        }
        const start = clamp(trimPreview?.start ??
            0, 0, safeVideoDuration);
        const end = clamp(trimPreview?.end ??
            (start +
                DEFAULT_PREVIEW_LENGTH), start, safeVideoDuration);
        setTrimStart(start);
        setTrimEnd(end);
        setSliderValues([
            start,
            start,
            end
        ]);
        if (lastCommittedCoverRef
            .current ===
            null) {
            lastCommittedCoverRef.current =
                initialCoverTime;
        }
    }, [
        safeVideoDuration,
        trimPreview?.start,
        trimPreview?.end
    ]);
    useEffect(() => {
        if (!playerReady ||
            !safeVideoDuration) {
            return;
        }
        setPlaying(false);
        setEnded(false);
if (videoPreviewActive) {
  const playheadTime = clamp(
    trimStart,
    0,
    safeVideoDuration
  )

  setCurrentTime(playheadTime)
  setScrubTime(playheadTime)

  setSliderValues([
    trimStart,
    playheadTime,
    trimEnd
  ])

  seekVideo(playheadTime)
  showPoster()
  return
}
        const coverTime = clamp(lastCommittedCoverRef
            .current ??
            initialCoverTime, 0, safeVideoDuration);
        setScrubTime(coverTime);
        setCurrentTime(coverTime);
        seekVideo(coverTime);
        showPoster();
    }, [
        videoPreviewActive,
        playerReady,
        safeVideoDuration
    ]);
    useEffect(() => {
        if (videoPreviewActive ||
            lastCommittedCoverRef
                .current ===
                null) {
            return;
        }
        if (Math.abs(scrubTime -
            lastCommittedCoverRef
                .current) >
            0.001) {
            setCoverState('toSet');
        }
    }, [
        scrubTime,
        videoPreviewActive
    ]);
    const handleSkip = (delta: number) => {
        if (!safeVideoDuration) {
            return;
        }
        setPlaying(false);
        setEnded(false);
        const target = clamp(scrubTime +
            delta, 0, safeVideoDuration);
        setScrubTime(target);
        setCurrentTime(target);
        hidePoster();
        seekVideo(target);
    };
    const handleCustomThumbnailUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target
            .files?.[0];
        if (!file ||
            !file.type.startsWith('image/')) {
            event.target.value =
                '';
            return;
        }
        setUploadedCustomThumbKey(null);
        setCustomThumbUploading(true);
        void uploadCustomThumbnail(file)
            .then(key => {
            setUploadedCustomThumbKey(key);
        })
            .catch(error => {
            console.error('[EditPreviewModal] custom thumbnail upload failed', error);
            setUploadedCustomThumbKey(null);
        })
            .finally(() => {
            setCustomThumbUploading(false);
        });
        const reader = new FileReader();
        reader.onload =
            () => {
                if (typeof reader.result !==
                    'string') {
                    return;
                }
                const localUrl = reader.result;
                setUploadedLocalPreview(localUrl);
                setCustomThumbs(previous => {
                    const captured = previous.find(thumb => typeof thumb.time ===
                        'number');
                    return [
                        ...(captured
                            ? [
                                captured
                            ]
                            : []),
                        {
                            url: localUrl,
                            sourceUrl: localUrl,
                            time: null
                        }
                    ];
                });
                setSelectedPreviewIdx(capturedCustomThumbs.length
                    ? 1 +
                        presetThumbs.length
                    : presetThumbs.length);
                setSelectedTime(null);
                setCropPosition({
                    x: 50,
                    y: 50
                });
                setCropDirty(false);
                setUploadedCropActive(true);
                setCoverState('done');
                setNoChanges(false);
                setPlaying(false);
                showPoster();
            };
        reader.readAsDataURL(file);
        event.target.value =
            '';
    };

    const handlePreviewThumbnailUpload = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const file = event.target.files?.[0]
      event.target.value = ''

      if (!file || !file.type.startsWith('image/')) return

      setCustomPreviewThumbKey(null)
      setCustomPreviewThumbUploading(true)
      setPreviewCoverIndex(null)

      const reader = new FileReader()

      reader.onload = () => {
        if (typeof reader.result !== 'string') return

        setCustomPreviewThumbUrl(reader.result)
        setPreviewCoverUrl(null)
      }

      reader.readAsDataURL(file)

      void uploadCustomPreviewThumbnail(file)
        .then(key => {
          setCustomPreviewThumbKey(key)
          setNoChanges(false)
        })
        .catch(error => {
          console.error(
            '[EditPreviewModal] custom preview thumbnail upload failed',
            error
          )

          setCustomPreviewThumbKey(null)
          setCustomPreviewThumbUrl(null)
        })
        .finally(() => {
          setCustomPreviewThumbUploading(false)
        })
    }

    const handleCapture = () => {
        const time = scrubTime;

        setPlaying(false);
        setSelectedTime(time);
        setNoChanges(false);

        lastCommittedCoverRef.current = time;

        const video = playerRef.current
            ?.getInternalPlayer() as HTMLVideoElement | null | undefined;

        if (!video?.videoWidth || !video.videoHeight) {
            return;
        }

        try {
            const canvas = document.createElement('canvas');

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const context = canvas.getContext('2d');

            if (!context) {
                return;
            }

            context.drawImage(
                video,
                0,
                0,
                canvas.width,
                canvas.height
            );

            const url = canvas.toDataURL('image/jpeg', 0.9);

            setCustomThumbs(previous => {
                /*
                * There is only ever one generated/captured thumbnail.
                *
                * If one already exists, this replaces it.
                *
                * Preserve an uploaded custom thumbnail if there is one.
                */
                const uploaded = previous.find(
                    thumb => thumb.time === null
                );

                return [
                    {
                        url,
                        time
                    },
                    ...(uploaded
                        ? [uploaded]
                        : [])
                ];
            });

            /*
            * Generated thumbnail is always the first item:
            *
            * [
            *   generated custom,
            *   preset 1,
            *   ...
            *   preset 8
            * ]
            */
            setSelectedPreviewIdx(0);

            setUploadedCropActive(false);
            setCoverState('done');

            showPoster();

            /*
            * We came here from:
            *
            * Thumbnail -> Generate Thumbnail
            *
            * Once generated, send the user back to the thumbnail
            * selection screen so they immediately see their new
            * custom thumbnail selected.
            */
            setThumbnailView('select');
        }
        catch (error) {
            console.error(
                '[EditPreviewModal] frame capture failed',
                error
            );
        }
    };
    const handleCropPointerDown = (event: React.PointerEvent<HTMLImageElement>) => {
        if (!uploadedCropActive) {
            return;
        }
        event.currentTarget
            .setPointerCapture(event.pointerId);
        cropPointerRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startPositionX: cropPosition.x,
            startPositionY: cropPosition.y
        };
    };
    const handleCropPointerMove = (event: React.PointerEvent<HTMLImageElement>) => {
        const drag = cropPointerRef.current;
        if (!drag ||
            drag.pointerId !==
                event.pointerId) {
            return;
        }
        const container = event.currentTarget
            .parentElement;
        if (!container) {
            return;
        }
        const rect = container.getBoundingClientRect();
        if (!rect.width ||
            !rect.height) {
            return;
        }
        const deltaX = ((event.clientX -
            drag.startX) /
            rect.width) *
            100;
        const deltaY = ((event.clientY -
            drag.startY) /
            rect.height) *
            100;
        setCropPosition({
            x: clamp(drag.startPositionX +
                deltaX, 0, 100),
            y: clamp(drag.startPositionY +
                deltaY, 0, 100)
        });
        setCropDirty(true);
    };
    const handleCropPointerUp = (event: React.PointerEvent<HTMLImageElement>) => {
        if (cropPointerRef.current
            ?.pointerId !==
            event.pointerId) {
            return;
        }
        cropPointerRef.current =
            null;
        try {
            event.currentTarget
                .releasePointerCapture(event.pointerId);
        }
        catch { }
    };
    const saveUploadedThumbnailCrop = () => {
        const image = uploadedCropImageRef
            .current;
        if (!image?.naturalWidth ||
            !image.naturalHeight) {
            return;
        }
        const targetRatio = CUSTOM_CROP_WIDTH /
            CUSTOM_CROP_HEIGHT;
        const sourceWidth = image.naturalWidth;
        const sourceHeight = image.naturalHeight;
        const sourceRatio = sourceWidth /
            sourceHeight;
        let sx = 0;
        let sy = 0;
        let sw = sourceWidth;
        let sh = sourceHeight;
        if (sourceRatio >
            targetRatio) {
            sh =
                sourceHeight;
            sw =
                sourceHeight *
                    targetRatio;
            sx =
                (sourceWidth -
                    sw) *
                    (cropPosition.x /
                        100);
        }
        else {
            sw =
                sourceWidth;
            sh =
                sourceWidth /
                    targetRatio;
            sy =
                (sourceHeight -
                    sh) *
                    (cropPosition.y /
                        100);
        }
        const canvas = document.createElement('canvas');
        canvas.width =
            CUSTOM_CROP_WIDTH;
        canvas.height =
            CUSTOM_CROP_HEIGHT;
        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }
        context.drawImage(image, sx, sy, sw, sh, 0, 0, CUSTOM_CROP_WIDTH, CUSTOM_CROP_HEIGHT);
        const croppedUrl = canvas.toDataURL('image/jpeg', 0.9);
        setUploadedLocalPreview(croppedUrl);
        setCustomThumbs(previous => previous.map(thumb => {
            if (thumb.time !==
                null) {
                return thumb;
            }
            return {
                ...thumb,
                sourceUrl: thumb.sourceUrl ??
                    thumb.url,
                url: croppedUrl
            };
        }));
        setCropDirty(false);
        setNoChanges(false);
        setUploadedCropActive(false);
        showPoster();
    };
useEffect(() => {
  if (!playing || !safeVideoDuration || usingCustomPreview) return;
  const video = playerRef.current?.getInternalPlayer() as HTMLVideoElement | null
  if (!video) return
  let frameId = 0
  const update = (_now: number, metadata: VideoFrameCallbackMetadata) => {
    const time = clamp(
      metadata.mediaTime,
      videoPreviewActive ? trimStart : 0,
      videoPreviewActive ? trimEnd : safeVideoDuration
    )
    setCurrentTime(time)
    if (videoPreviewActive) {
      setScrubTime(time)
      setSliderValues(previous => [
        previous[0],
        time,
        previous[2]
      ])
    } else {
      setScrubTime(time)
    }
    frameId = video.requestVideoFrameCallback(update)
  }
  frameId = video.requestVideoFrameCallback(update)
  return () => {
    video.cancelVideoFrameCallback(frameId)
  }
}, [
  playing,
  videoPreviewActive,
  safeVideoDuration,
  trimStart,
  trimEnd
])

useEffect(() => {
  customPreviewBlobRef.current = customPreviewBlob;
}, [customPreviewBlob]);

useEffect(() => {
  return () => {
    previewUploadControllerRef.current?.abort();

    const blob = customPreviewBlobRef.current;

    if (
      !keepPreviewBlobRef.current &&
      blob?.startsWith('blob:')
    ) {
      URL.revokeObjectURL(blob);
    }
  };
}, []);

const handlePreviewVideoUpload = (
  event: React.ChangeEvent<HTMLInputElement>
) => {
  const file = event.target.files?.[0];
  event.target.value = '';

  if (!file || !file.type.startsWith('video/')) return;

  previewUploadControllerRef.current?.abort();

  if (customPreviewBlob?.startsWith('blob:')) {
    URL.revokeObjectURL(customPreviewBlob);
  }

  const controller = new AbortController();
  previewUploadControllerRef.current = controller;

  setPlaying(false);
  setCustomPreviewBlob(URL.createObjectURL(file));
  setExistingPreviewUrl(null);
  setPreviewVideoId(null);
  setCustomPreviewDuration(0);
  setCustomPreviewUploading(true);
  setNoChanges(false);
  setVideoPreviewEnabled(true)

  if (!uploadCustomPreview) {
    setCustomPreviewUploading(false);
    return;
  }

  void uploadCustomPreview(file, controller.signal)
    .then(id => {
      if (!controller.signal.aborted) {
        setPreviewVideoId(id);
      }
    })
    .catch(error => {
      if (!controller.signal.aborted) {
        console.error(
          '[EditPreviewModal] preview video upload failed',
          error
        );
        setPreviewVideoId(null);
      }
    })
    .finally(() => {
      if (previewUploadControllerRef.current === controller) {
        previewUploadControllerRef.current = null;
        setCustomPreviewUploading(false);
      }
    });
};
const storyboardFrameStyle = (
  frame: StoryboardFrame
): React.CSSProperties => {
  if (!storyboard) return {}

  const col = frame.tileIndex % storyboard.tile_width
  const row = Math.floor(
    frame.tileIndex / storyboard.tile_width
  )

  return {
    backgroundImage: `url("${frame.url}")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${storyboard.tile_width * 100}% ${
      storyboard.tile_height * 100
    }%`,
    backgroundPosition: `${
      storyboard.tile_width > 1
        ? (col / (storyboard.tile_width - 1)) * 100
        : 0
    }% ${
      storyboard.tile_height > 1
        ? (row / (storyboard.tile_height - 1)) * 100
        : 0
    }%`
  }
}

const cancelPreviewUpload = () => {
  previewUploadControllerRef.current?.abort();
  previewUploadControllerRef.current = null;

  if (customPreviewBlob?.startsWith('blob:')) {
    URL.revokeObjectURL(customPreviewBlob);
  }

  setCustomPreviewBlob(null);
  setPreviewVideoId(null);
  setCustomPreviewDuration(0);
  setCustomPreviewUploading(false);
  setPlaying(false);
  setNoChanges(false);
};

const handlePreviewPlayToggle = () => {
  hidePoster();

  if (usingCustomPreview) {
    setEnded(false);
    setPlaying(previous => !previous);
    return;
  }
  if (playing) {
    setPlaying(false)
    return
  }
  if (ended) {
    const restartTime = videoPreviewActive ? trimStart : 0
    seekVideo(restartTime)
    setCurrentTime(restartTime)
    setScrubTime(restartTime)
    if (videoPreviewActive) {
      setSliderValues(previous => [
        previous[0],
        restartTime,
        previous[2]
      ])
    }
    setEnded(false)
  }
  setPlaying(true)
}
    const handlePreviewSliderChange = (value: number[], index: number) => {
        if (!safeVideoDuration) {
            return;
        }
        setNoChanges(false);
        setEnded(false);
        setPlaying(false);
        const start = trimStart;
        const end = trimEnd;
        const playhead = sliderValues[1];
        if (index === 0) {
            const nextStart = clamp(value[0] ??
                start, 0, end);
            const nextPlayhead = clamp(playhead, nextStart, end);
            setTrimStart(nextStart);
            setCurrentTime(nextPlayhead);
            setSliderValues([
                nextStart,
                nextPlayhead,
                end
            ]);
            return;
        }
        if (index === 1) {
            const nextPlayhead = clamp(value[1] ??
                playhead, start, end);
            setCurrentTime(nextPlayhead);
            setScrubTime(nextPlayhead);
            setSliderValues([
                start,
                nextPlayhead,
                end
            ]);
            scrubTo(nextPlayhead)
            return;
        }
        const nextEnd = clamp(value[2] ??
            end, start, safeVideoDuration);
        const nextPlayhead = clamp(playhead, start, nextEnd);
        setTrimEnd(nextEnd);
        setCurrentTime(nextPlayhead);
        setSliderValues([
            start,
            nextPlayhead,
            nextEnd
        ]);
    };
    const handleSharedSliderChange = (value: number | number[], index: number) => {
        hidePoster();
        if (uploadedCropActive) {
            setUploadedCropActive(false);
        }
        if (videoPreviewActive) {
            handlePreviewSliderChange(value as number[], index);
            return;
        }
        if (!safeVideoDuration) {
            return;
        }
        const rawTime = Array.isArray(value)
            ? value[0]
            : value;
        const target = clamp(rawTime ??
            scrubTime, 0, safeVideoDuration);
        setNoChanges(false);
        setPlaying(false);
        setEnded(false);
        setScrubTime(target);
        setCurrentTime(target);
        scrubTo(target)
    };
    const handleSharedSliderAfterChange = (
      value: number | number[],
      index: number
    ) => {
      if (scrubIdleTimerRef.current) {
        clearTimeout(scrubIdleTimerRef.current)
        scrubIdleTimerRef.current = null
      }
      scrubRef.current.assisted = false
      scrubRef.current.speed = 0
      scrubRef.current.frameIndex = null
      setActiveScrubFrame(null)
      if (!safeVideoDuration) return
      if (videoPreviewActive && index !== 1) {
        return
      }
      let target = Array.isArray(value)
        ? videoPreviewActive
          ? value[1]
          : value[0]
        : value
      target = clamp(target, 0, safeVideoDuration)
      setScrubTime(target)
      setCurrentTime(target)
      if (videoPreviewActive) {
        setSliderValues(previous => [
          previous[0],
          target,
          previous[2]
        ])
      }
      seekVideo(target)
    }
const handleProgress = ({ playedSeconds }: {
  playedSeconds: number
}) => {
  if (
    videoPreviewActive &&
    playing &&
    playedSeconds >= trimEnd
  ) {
    setEnded(true)
    setPlaying(false)
    setCurrentTime(trimEnd)
    setScrubTime(trimEnd)
    setSliderValues([
      trimStart,
      trimEnd,
      trimEnd
    ])
    seekVideo(trimEnd)
  }
}
    const selectThumbnail = (thumbnail: typeof displayedThumbs[number], index: number) => {
        setNoChanges(false);
        setSelectedPreviewIdx(index);
        setPlaying(false);
        setEnded(false);
        if (thumbnail.type ===
            'custom') {
            setCoverState('done');
            setSelectedTime(thumbnail.time);
            if (typeof thumbnail.time ===
                'number') {
                setUploadedCropActive(false);
                setScrubTime(thumbnail.time);
                setCurrentTime(thumbnail.time);
                lastCommittedCoverRef.current =
                    thumbnail.time;
                seekVideo(thumbnail.time);
                showPoster();
                return;
            }
            setCropPosition({
                x: 50,
                y: 50
            });
            setCropDirty(false);
            setUploadedCropActive(true);
            showPoster();
            return;
        }
        setUploadedCropActive(false);
        const time = typeof thumbnail.time ===
            'number'
            ? clamp(thumbnail.time, 0, safeVideoDuration ||
                thumbnail.time)
            : 0;
        setCoverState('initial');
        setSelectedTime(null);
        setScrubTime(time);
        setCurrentTime(time);
        lastCommittedCoverRef.current =
            time;
        seekVideo(time);
        showPoster();
    };
    const sliderWidth = sliderRef.current
        ?.getBoundingClientRect()
        .width ??
        0;
    const rootFontSize = typeof window !==
        'undefined'
        ? parseFloat(window
            .getComputedStyle(document.documentElement)
            .fontSize)
        : 16;
    const edgeThresholdSeconds = sliderWidth > 0 &&
        safeVideoDuration > 0
        ? (rootFontSize /
            sliderWidth) *
            safeVideoDuration
        : 0;
    const leftFlush = videoPreviewActive &&
        trimStart <=
            edgeThresholdSeconds;
    const rightFlush = videoPreviewActive &&
        safeVideoDuration > 0 &&
        safeVideoDuration -
            trimEnd <=
            edgeThresholdSeconds;
    const previewCoverDisplayUrl =
      customPreviewThumbUrl ??
      previewCoverUrl ??
      activeThumb

    const posterUrl = activeThumb ?? undefined

    const choosingPreviewCover =
      videoPreviewActive &&
      videoPreviewView === 'cover'

    return (
      <div
        className={`editPreviewModal ${
          deviceOrientation?.type.includes('landscape')
            ? 'editPreviewModal--landscape'
            : ''
        }`}
      >
        <div className='editPreviewModal__content'>
          <input
            ref={previewThumbInputRef}
            type='file'
            accept='image/*'
            hidden
            onChange={handlePreviewThumbnailUpload}
          />

          <div className='editPreviewModal__tabs'>
            <button
              type='button'
              className={`editPreviewModal__tab ${
                activeTab === 'thumbnail' ? 'editPreviewModal__tab--active' : ''
              }`}
              onClick={() => {
                setPlaying(false)
                setVideoPreviewView('editor')
                setActiveTab('thumbnail')
                setThumbnailView('select')
              }}
            >
              <MdOutlineImageIcon className='editPreviewModal__tabIcon' />
              <span>Thumbnail</span>
            </button>
            <button
              type='button'
              className={`editPreviewModal__tab ${
                activeTab === 'videoPreview' ? 'editPreviewModal__tab--active' : ''
              }`}
              onClick={() => {
                setPlaying(false)
                setVideoPreviewView('editor')
                setActiveTab('videoPreview')
              }}
            >
              <MdPlayCircleOutlineIcon className='editPreviewModal__tabIcon' />
              <span>Video Preview</span>
            </button>
          </div>

          {activeTab === 'thumbnail' && thumbnailView === 'select' && (
            <div className='editPreviewModal__thumbnailPanel'>
              <div className='editPreviewModal__sectionHeader'>
                <div className='editPreviewModal__sectionTitle'>Cover Thumbnail</div>
                <div className='editPreviewModal__sectionDescription'>
                  This will be the main cover shown on your post.
                </div>
              </div>

              <div
                className={`editPreviewModal__content__list ${
                  orientation?.toLowerCase() === 'portrait'
                    ? 'editPreviewModal__content__list--portrait'
                    : 'editPreviewModal__content__list--landscape'
                }`}
              >
                {!thumbsQueryDone || !thumbsReady ? (
                  <div className='editPreviewModal__thumbsLoaderOnly'>
                    <BeatLoader size={10} />
                  </div>
                ) : (
                  displayedThumbs.map((thumbnail, index) => (
                    <button
                      type='button'
                      key={`${thumbnail.url}-${index}`}
                      className={`editPreviewModal__content__list__item ${
                        index === selectedPreviewIdx
                          ? 'editPreviewModal__content__list__item--active'
                          : ''
                      }`}
                      onClick={() => selectThumbnail(thumbnail, index)}
                    >
                      <div className='editPreviewModal__content__list__item__check'>
                        {index === selectedPreviewIdx && <IconCheckmarkSm />}
                      </div>

                      <img
                        src={thumbnail.url}
                        alt='thumbnail'
                        draggable={false}
                      />

                      {thumbnail.type === 'custom' &&
                        thumbnail.time === null &&
                        customThumbUploading && (
                          <div className='editPreviewModal__customThumbUploading'>
                            <BeatLoader size={6} />
                          </div>
                        )}
                    </button>
                  ))
                )}

                <button
                  type='button'
                  className='editPreviewModal__thumbnailAction'
                  onClick={() => {
                    setPlaying(false);
                    setUploadedCropActive(false);
                    setThumbnailView('generate');
                  }}
                >
                  <span className='editPreviewModal__thumbnailActionIcon'>✦</span>
                  <span>Generate</span>
                  <span>Thumbnail</span>
                </button>

                <button
                  type='button'
                  className='editPreviewModal__thumbnailAction'
                  onClick={() => customThumbnailInputRef.current?.click()}
                >
                  <span className='editPreviewModal__thumbnailActionIcon'>↑</span>
                  <span>Upload</span>
                  <span>Thumbnail</span>
                </button>

                <input
                  ref={customThumbnailInputRef}
                  type='file'
                  accept='image/*'
                  hidden
                  onChange={handleCustomThumbnailUpload}
                />
              </div>
            </div>
          )}

{videoPreviewActive && videoPreviewView === 'cover' && (
  <div className='editPreviewModal__thumbnailPanel'>
    <div className='editPreviewModal__sectionHeader'>
      <div className='editPreviewModal__sectionTitle'>
        Preview Cover
      </div>

      <div className='editPreviewModal__sectionDescription'>
        Choose the cover shown for your video preview.
      </div>
    </div>

    <div
      className={`editPreviewModal__content__list ${
        orientation?.toLowerCase() === 'portrait'
          ? 'editPreviewModal__content__list--portrait'
          : 'editPreviewModal__content__list--landscape'
      }`}
    >
    {!thumbsQueryDone || !thumbsReady ? (
      <div className='editPreviewModal__thumbsLoaderOnly'>
        <BeatLoader size={10} />
      </div>
    ) : (
      <>
        {customPreviewThumbUrl && (
          <button
            type='button'
            className='editPreviewModal__content__list__item editPreviewModal__content__list__item--active'
            onClick={() => {
              setPreviewCoverIndex(null)
              setPreviewCoverUrl(null)
            }}
          >
            <div className='editPreviewModal__content__list__item__check'>
              <IconCheckmarkSm />
            </div>

            <img
              src={customPreviewThumbUrl}
              alt='Custom preview cover'
              draggable={false}
            />

            {customPreviewThumbUploading && (
              <div className='editPreviewModal__customThumbUploading'>
                <BeatLoader size={6} />
              </div>
            )}
          </button>
        )}

        {thumbnails.slice(0, 8).map((url, index) => (
          <button
            type='button'
            key={`preview-cover-${url}-${index}`}
            className={`editPreviewModal__content__list__item ${
              previewCoverIndex === index && !customPreviewThumbUrl
                ? 'editPreviewModal__content__list__item--active'
                : ''
            }`}
            onClick={() => {
              setPreviewCoverIndex(index)
              setPreviewCoverUrl(url)
              setCustomPreviewThumbKey(null)
              setCustomPreviewThumbUrl(null)
            }}
          >
            <div className='editPreviewModal__content__list__item__check'>
              {previewCoverIndex === index && !customPreviewThumbUrl && (
                <IconCheckmarkSm />
              )}
            </div>

            <img
              src={url}
              alt='Preview cover'
              draggable={false}
            />
          </button>
        ))}
      </>
    )}

      <button
        type='button'
        className='editPreviewModal__thumbnailAction'
        disabled={customPreviewThumbUploading}
        onClick={() => previewThumbInputRef.current?.click()}
      >
        <span className='editPreviewModal__thumbnailActionIcon'>
          {customPreviewThumbUploading ? (
            <BeatLoader size={4} />
          ) : (
            '↑'
          )}
        </span>
        <span>
          {customPreviewThumbUploading ? 'Uploading' : 'Upload'}
        </span>
        <span>Preview Cover</span>
      </button>
    </div>
  </div>
)}

{editorActive && !(videoPreviewActive && videoPreviewView === 'cover') && (
  <div
    className={`editPreviewModal__editorPanel ${
      videoPreviewActive
        ? 'editPreviewModal__videoPreviewPanel'
        : 'editPreviewModal__generateThumbnailPanel'
    }`}
  >
    {generateThumbnailActive && (
      <div className='editPreviewModal__sectionHeader'>
        <div className='editPreviewModal__sectionTitle'>
          Generate Thumbnail
        </div>
        <div className='editPreviewModal__sectionDescription'>
          Find the perfect frame from your video or upload your own.
        </div>
      </div>
    )}

    <div
      className={`editPreviewModal__top ${
        orientation?.toLowerCase() === 'portrait'
          ? 'editPreviewModal__top--portrait'
          : 'editPreviewModal__top--landscape'
      }`}
    >
      <div className='editPreviewModal__top__cover'>
        <div className='editPreviewModal__playerWrapper'>
          <div className='video-player'>
          {canMountPlayer && (
              <ReactPlayer
                  ref={playerRef}
                  url={playerUrl ?? undefined}
                  width='100%'
                  height='100%'
                  controls={false}
                  muted={false}
                  playing={playing && !customPreviewUploading}
                  progressInterval={33}
                  config={{
                      file: {
                          forceVideo: true,
                          attributes: {
                              ...(isProtectedHls
                                  ? { crossOrigin: 'use-credentials' }
                                  : {})
                          },
                          hlsOptions: {
                              xhrSetup: (xhr: XMLHttpRequest) => {
                                  xhr.withCredentials = isProtectedHls
                              }
                          }
                      }
                  }}
                  onReady={() => {
                      setPlayerReady(true)
                  }}
                  onDuration={value => {
                      if (!Number.isFinite(value) || value <= 0) return

                      if (usingCustomPreview) {
                          setCustomPreviewDuration(value)
                          return
                      }

                      setVideoDuration(value)
                  }}
                  onPlay={() => {
                      setEnded(false)
                      setPlaying(true)
                  }}
                  onPause={() => {
                      setPlaying(false)
                  }}
                  onEnded={() => {
                      setEnded(true)
                      setPlaying(false)

                      if (videoPreviewActive && !usingCustomPreview) {
                          setCurrentTime(trimEnd)
                      }
                  }}
                  onProgress={handleProgress}
              />
          )}
          </div>

          {customPreviewUploading && (
            <div className='editPreviewModal__previewUploading'>
              <div className='editPreviewModal__previewUploadingCenter'>
                <BeatLoader size={8} color='#fff' />
                <span>Uploading...</span>
              </div>
              <button
                type='button'
                className='editPreviewModal__previewCancelUpload'
                onClick={cancelPreviewUpload}
              >
                Cancel
              </button>
            </div>
          )}
          {customPreviewUploading && (
            <div className='editPreviewModal__previewUploading'>
              <div className='editPreviewModal__previewUploadingCenter'>
                <BeatLoader size={8} color='#fff' />
                <span>Uploading...</span>
              </div>

              <button
                type='button'
                className='editPreviewModal__previewCancelUpload'
                onClick={cancelPreviewUpload}
              >
                Cancel
              </button>
            </div>
          )}
          {!usingCustomPreview && (
            <>
              {uploadedCropActive && uploadedCropSource ? (
                <img
                  ref={uploadedCropImageRef}
                  src={uploadedCropSource}
                  alt=''
                  className='editPreviewModal__uploadedCropImage'
                  draggable={false}
                  style={{
                    objectPosition: `${cropPosition.x}% ${cropPosition.y}%`
                  }}
                  onPointerDown={handleCropPointerDown}
                  onPointerMove={handleCropPointerMove}
                  onPointerUp={handleCropPointerUp}
                  onPointerCancel={handleCropPointerUp}
                />
                ) : activeScrubFrame ? (
                  'tileIndex' in activeScrubFrame ? (
                    <div
                      className='editPreviewModal__scrubStoryboardFrame'
                      style={storyboardFrameStyle(activeScrubFrame)}
                    />
                  ) : (
                    <img
                      src={activeScrubFrame.url}
                      alt=''
                      className='editPreviewModal__scrubFrame'
                      draggable={false}
                    />
                  )
                ) : (
                showPosterOverlay &&
                posterUrl && (
                  <img
                    src={posterUrl}
                    alt=''
                    className='editPreviewModal__posterOverlay'
                    draggable={false}
                  />
                )
              )}
            </>
          )}

          {!videoPreviewActive && !uploadedCropActive && (
            <div className='editPreviewModal__controls'>
              <div className='editPreviewModal__setCoverRow'>
                <button
                  type='button'
                  onClick={handleCapture}
                  className={`editPreviewModal__setCoverBtn ${coverState}`}
                >
                  Generate Thumb
                </button>
              </div>

              <div className='editPreviewModal__bottomRow'>
                <button
                  type='button'
                  onClick={() => handleSkip(-FRAME_SKIP_SECONDS)}
                  className='editPreviewModal__skipBtn editPreviewModal__skipBtn--left'
                >
                  <GrPrevIcon
                    className='doubleArrow first'
                    size={18}
                  />
                  <GrPrevIcon
                    className='doubleArrow second'
                    size={18}
                  />
                </button>

                <span />

                <button
                  type='button'
                  onClick={() => handleSkip(FRAME_SKIP_SECONDS)}
                  className='editPreviewModal__skipBtn editPreviewModal__skipBtn--right'
                >
                  <MdNextIcon
                    className='doubleArrow first'
                    size={18}
                  />
                  <MdNextIcon
                    className='doubleArrow second'
                    size={18}
                  />
                </button>
              </div>
            </div>
          )}

          {!videoPreviewActive && uploadedCropActive && (
            <button
              type='button'
              className='editPreviewModal__saveCropBtn'
              onClick={saveUploadedThumbnailCrop}
            >
              Save Crop
            </button>
          )}

          {videoPreviewActive && !usingCustomPreview ? (
            <>
              <div className='editPreviewModal__duration editPreviewModal__duration--selection'>
                <span>{formatVideoTime(trimStart)}</span>
                <span className='editPreviewModal__durationSeparator'>
                  {' - '}
                </span>
                <span
                  className={
                    previewTooLong
                      ? 'editPreviewModal__durationTime editPreviewModal__durationTime--invalid'
                      : 'editPreviewModal__durationTime'
                  }
                >
                  {formatVideoTime(trimEnd)}
                </span>
              </div>

              <div className='editPreviewModal__totalDuration'>
                {formatVideoTime(safeVideoDuration)}
              </div>
            </>
          ) : videoPreviewActive &&
            usingCustomPreview &&
            customPreviewDuration > 0 ? (
            <div className='editPreviewModal__totalDuration'>
              {formatVideoTime(customPreviewDuration)}
            </div>
          ) : !uploadedCropActive ? (
            <div className='editPreviewModal__duration'>
              {`${formatVideoTime(scrubTime)} / ${formatVideoTime(
                safeVideoDuration
              )}`}
            </div>
          ) : null}

          {uploadedCropActive && !usingCustomPreview && (
            <div className='editPreviewModal__cropHint'>
              Drag to reposition
            </div>
          )}

          {!uploadedCropActive && !customPreviewUploading && (
            <button
              type='button'
              className={`editPreviewModal__player__icon editPreviewModal__player__icon${
                playing ? '--playing' : '--notPlaying'
              }`}
              onClick={handlePreviewPlayToggle}
              aria-label={playing ? 'Pause video' : 'Play video'}
            >
              {playing ? (
                <IconPauseFill />
              ) : (
                <IconPlayOutline color='#fff' />
              )}
            </button>
          )}
        </div>
      </div>

      {!usingCustomPreview && (
        <div className='editVideoModal editVideoModal--inline'>
          <div
            ref={sliderRef}
            className={`editVideoModal__slider ${
              videoPreviewActive && trimStart > 0
                ? 'slid-right'
                : ''
            } ${
              videoPreviewActive &&
              trimEnd < safeVideoDuration
                ? 'slid-left'
                : ''
            } ${leftFlush ? 'left-flush' : ''} ${
              rightFlush ? 'right-flush' : ''
            }`}
          >
            <Swiper
              slidesPerView={6}
              resistance
              freeMode
              modules={[FreeMode]}
              allowTouchMove={false}
            >
              {sliderThumbs.map((thumbnail, index) => (
                <SwiperSlide key={`${thumbnail.url}-${index}`}>
                  <div className='editVideoModal__slider__image'>
                    <img
                      src={thumbnail.url}
                      alt=''
                      draggable={false}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            <div className='editVideoModal__slider__wrapper'>
              <ReactSlider
                className={`horizontal-slider ${
                  videoPreviewActive
                    ? 'horizontal-slider--preview'
                    : 'horizontal-slider--cover'
                }`}
                trackClassName='slider-track'
                value={
                  videoPreviewActive
                    ? sliderValues
                    : scrubTime
                }
                min={0}
                max={safeVideoDuration || 1}
                step={0.01}
                minDistance={0}
                pearling={false}
                onChange={handleSharedSliderChange}
                onAfterChange={handleSharedSliderAfterChange}
                renderThumb={(props, state) => {
                  // eslint-disable-next-line react/prop-types
                  const { key: thumbKey, ...thumbProps } = props;
                  const visualIndex = videoPreviewActive ? state.index : 1;

                  return (
                    <div
                      key={thumbKey}
                      {...thumbProps}
                      className={`slider-thumb slider-thumb-${visualIndex}`}
                    />
                  );
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>

    {generateThumbnailActive && (
      <div className='editPreviewModal__generatePlaceholder' />
    )}

    {videoPreviewActive && (
      <div className='editPreviewModal__previewClipSection'>
<div className='editPreviewModal__videoPreviewSettings'>
  <div className='editPreviewModal__videoPreviewInfo'>
    <div className='editPreviewModal__videoPreviewIcon'>
      <MdPlayCircleOutlineIcon size={30} />
    </div>

    <div className='editPreviewModal__videoPreviewCopy'>
      <strong>Enable Video Preview</strong>
      <span>Show a short preview clip with your post.</span>
    </div>
  </div>

  <button
    type='button'
    className='editPreviewModal__videoPreviewUpload'
    aria-label={
      activePreviewVideo
        ? 'Replace custom preview video'
        : 'Upload custom preview video'
    }
    onClick={() => previewVideoInputRef.current?.click()}
  >
    <MdOutlineFileUploadIcon size={23} />
  </button>

  <div
    className='editPreviewModal__videoPreviewSwitch'
    onClick={event => event.stopPropagation()}
  >
    <SwitchButton
      active={videoPreviewEnabled}
      toggle={() => {
        setPlaying(false)
        setNoChanges(false)
        setVideoPreviewEnabled(previous => !previous)
      }}
    />
  </div>
</div>
<input
  ref={previewVideoInputRef}
  type='file'
  accept='video/*'
  hidden
  onChange={handlePreviewVideoUpload}
/>

        <div className='editPreviewModal__coverPreviewCard'>
          <div
            className={`editPreviewModal__coverPreviewImage ${
              orientation?.toLowerCase() === 'portrait'
                ? 'editPreviewModal__coverPreviewImage--portrait'
                : ''
            }`}
          >
            {previewCoverDisplayUrl && (
              <img
                src={previewCoverDisplayUrl}
                alt='Selected preview cover'
                draggable={false}
              />
            )}
          </div>

          <div className='editPreviewModal__coverPreviewContent'>
            <div className='editPreviewModal__coverPreviewTitle'>
              Preview Cover
            </div>

            <div className='editPreviewModal__coverPreviewDescription'>
              Use the full video cover or choose a custom one
            </div>

            <div className='editPreviewModal__coverPreviewActions'>
              <button
                type='button'
                className='editPreviewModal__coverUploadButton'
                aria-label='Upload preview cover'
                onClick={() => previewThumbInputRef.current?.click()}
              >
                <MdOutlineFileUploadIcon size={19} />
              </button>

              <button
                type='button'
                className='editPreviewModal__changeCoverButton'
                onClick={() => {
                  setPlaying(false)
                  setVideoPreviewView('cover')
                }}
              >
                <MdOutlineImageIcon size={18} />
                <span>Change</span>
              </button>
            </div>
          </div>

          <button
            type='button'
            className='editPreviewModal__coverPreviewArrow'
            aria-label='Change preview cover'
            onClick={() => {
              setPlaying(false)
              setVideoPreviewView('cover')
            }}
          >
            <MdNextIcon size={24} />
          </button>
        </div>
      </div>
    )}
  </div>
)}
        </div>

      <div className='editPreviewModal__footer'>
        <Button
          text={choosingPreviewCover ? 'Back' : t('cancel')}
          color='grey'
          font='mont-14-normal'
          width='13'
          height='4'
          customClass='editpreviewmodal_cancel__button'
          clickFn={() => {
            if (choosingPreviewCover) {
              setVideoPreviewView('editor')
              return
            }

            cancelCb()
          }}
        />
        <Button
          text={t('save')}
          color='black'
          font='mont-14-normal'
          width='13'
          height='4'
          customClass='dateModal_date__button'
          disabled={
            choosingPreviewCover
              ? (
                  customPreviewThumbUploading ||
                  (!!customPreviewThumbUrl && !customPreviewThumbKey)
                )
              : (
                  customPreviewThumbUploading ||
                  (!!customPreviewThumbUrl && !customPreviewThumbKey) ||
                  customPreviewUploading ||
                  (!!customPreviewBlob && !previewVideoId) ||
                  (videoPreviewEnabled &&
                    !usingCustomPreview &&
                    !previewIsValidLength) ||
                  (selectedUploadedCustomThumb &&
                    (customThumbUploading || !uploadedCustomThumbKey))
                )
          }
          clickFn={() => {
            setPlaying(false)

            if (choosingPreviewCover) {
              setNoChanges(false)
              setVideoPreviewView('editor')
              return
            }

            const usingCustomCover = selectedThumbItem?.type === 'custom'

            const effectiveSelectedTime = usingCustomCover
              ? selectedThumbItem?.time ?? null
              : null;

            const effectiveSelectedIndex =
              selectedThumbItem?.type === 'preset'
                ? selectedThumbItem.originalIndex
                : undefined;

            if (customPreviewBlob?.startsWith('blob:')) {
              keepPreviewBlobRef.current = true;
            }

            confirmCb({
              defaultPrev: defaultPreview,
              activeThumbnail: activeThumb,
              thumbType: videoPreviewEnabled ? 'clip' : 'thumb',
              previewOn: videoPreviewEnabled,
              previewThumbnailIndex:
                customPreviewThumbKey
                  ? null
                  : previewCoverIndex,

              customPreviewThumbKey:
                customPreviewThumbKey ?? null,

              customPreviewThumbnailUrl:
                customPreviewThumbUrl ?? null,
              selectedIndex: effectiveSelectedIndex,
              selectedTime: effectiveSelectedTime,
              customThumbKey: selectedUploadedCustomThumb
                ? uploadedCustomThumbKey
                : null,
              customThumbCrop: selectedUploadedCustomThumb
                ? {
                    x: cropPosition.x,
                    y: cropPosition.y
                  }
                : null,
              previewVideoId: usingCustomPreview
                ? previewVideoId
                : null,
              customPreviewUrl: usingCustomPreview
                ? activePreviewVideo
                : null,
              clip:
                videoPreviewEnabled && !usingCustomPreview
                  ? {
                      start: trimStart,
                      end: trimEnd
                    }
                  : undefined
            });
          }}
        />
      </div>
      </div>
    );
};

export default EditPreviewModal;
